import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { SupportService } from '../services/support.service';
import { QBuiltService } from 'src/app/modules/qbuilt/services/qbuilt-service';
import { ChecklistValues } from 'src/app/modules/qbuilt/models/checklist.model';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { localStorageConstants } from 'src/app/shared/services';

interface ZoneConfig {
  Id: number;
  Name: string;
  HBGFacilityNumber: string;
}

interface SerialOption {
  serialNumber: string;
  raw: any;
}

@Component({
  selector: 'app-qbuilt-support',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDividerModule,
  ],
  templateUrl: './qbuilt-support.component.html',
  styleUrl: './qbuilt-support.component.scss',
})
export class QbuiltSupportComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  private activeInspectionData: any[] = [];

  // ── DynamoDB section state ─────────────────────────────────────────────────
  plants: string[] = [];
  zones: ZoneConfig[] = [];
  serials: SerialOption[] = [];

  selectedPlant: string = '';
  selectedZone: ZoneConfig | null = null;
  selectedSerial: SerialOption | null = null;

  loading = false;
  deleting = false;
  noDataForPlant = false;
  noDataForZone = false;

  // ── Database (permanent) delete section state ──────────────────────────────
  dbPlants: string[] = [];
  dbZones: ZoneConfig[] = [];
  dbSerials: string[] = [];

  dbSelectedPlant: string = '';
  dbSelectedZone: ZoneConfig | null = null;
  dbSelectedSerial: string = '';

  dbLoadingZones = false;
  dbLoadingSerials = false;
  dbDeleting = false;
  dbNoDataForPlant = false;
  dbNoDataForZone = false;

  // ── Create Checklist section state ─────────────────────────────────────────
  checklistJson = '';
  isCreatingChecklist = false;
  createChecklistError: string | null = null;
  createChecklistSuccess = false;

  constructor(
    private supportService: SupportService,
    private qBuiltService: QBuiltService,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
  ) {
    const allPlants = this.loadPlants();
    this.plants = allPlants;
    this.dbPlants = allPlants;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPlants(): string[] {
    try {
      const facilities = JSON.parse(localStorage.getItem(localStorageConstants.USER_FACILITIES_KEY) || '[]');
      return facilities
        .map((f: any) => f?.Facility?.KeyChain?.LegacyId as string)
        .filter((id: string) => !!id);
    } catch {
      return [];
    }
  }

  // ── DynamoDB section handlers ──────────────────────────────────────────────

  onPlantChange(plant: string): void {
    this.selectedPlant = plant;
    this.selectedZone = null;
    this.selectedSerial = null;
    this.zones = [];
    this.serials = [];
    this.activeInspectionData = [];
    this.noDataForPlant = false;
    this.noDataForZone = false;

    this.loading = true;

    this.supportService
      .getZonesByFacility(plant)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.loading = false;
          this.zones = zones ?? [];
          if (this.zones.length === 0) this.noDataForPlant = true;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Failed to load zones.', 'Close', { duration: 4000 });
        },
      });
  }

  onZoneChange(zone: ZoneConfig): void {
    this.selectedZone = zone;
    this.selectedSerial = null;
    this.serials = [];
    this.activeInspectionData = [];
    this.noDataForZone = false;

    const pk = `${this.selectedPlant}-${zone.Id}`;
    this.loading = true;

    this.supportService
      .getInspectionProgress(pk)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (record) => {
          this.loading = false;
          if (!record) {
            this.noDataForZone = true;
            return;
          }
          try {
            this.activeInspectionData = typeof record.InspectionData === 'string'
              ? JSON.parse(record.InspectionData)
              : record.InspectionData ?? [];
          } catch {
            this.activeInspectionData = [];
          }
          this.serials = this.activeInspectionData.map((item) => ({
            serialNumber: item.serialNumber ?? item.SerialNumber ?? '(unknown)',
            raw: item,
          }));
          if (this.serials.length === 0) this.noDataForZone = true;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Failed to load inspection data.', 'Close', { duration: 4000 });
        },
      });
  }

  onSerialChange(serial: SerialOption): void {
    this.selectedSerial = serial;
  }

  deleteSerial(): void {
    if (!this.selectedSerial || !this.selectedZone || !this.selectedPlant) return;

    const pk = `${this.selectedPlant}-${this.selectedZone.Id}`;
    const serial = this.selectedSerial.serialNumber;

    const ref = this.dialogService.openDialog(
      'Confirm Delete Inspection',
      `Remove serial #${serial} from zone "${this.selectedZone.Name}" (plant ${this.selectedPlant})? This cannot be undone.`,
      'Cancel',
      'Delete',
      true,
    );

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;

      this.deleting = true;
      const remaining = this.activeInspectionData.filter(
        (item) => (item.serialNumber ?? item.SerialNumber) !== serial,
      );

      const action$ = remaining.length === 0
        ? this.supportService.deleteInspection(pk)
        : this.supportService.saveInspectionProgress(pk, remaining);

      action$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.deleting = false;
          this.snackBar.open(`Serial #${serial} removed successfully.`, 'Close', { duration: 4000 });

          this.activeInspectionData = remaining;
          this.serials = remaining.map((item) => ({
            serialNumber: item.serialNumber ?? item.SerialNumber ?? '(unknown)',
            raw: item,
          }));
          this.selectedSerial = null;

          if (remaining.length === 0) {
            this.noDataForZone = true;
          }
        },
        error: () => {
          this.deleting = false;
          this.snackBar.open('Delete failed. Please try again.', 'Close', { duration: 4000 });
        },
      });
    });
  }

  // ── Database (permanent) delete section handlers ───────────────────────────

  onDbPlantChange(plant: string): void {
    this.dbSelectedPlant = plant;
    this.dbSelectedZone = null;
    this.dbSerials = [];
    this.dbSelectedSerial = '';
    this.dbZones = [];
    this.dbNoDataForPlant = false;
    this.dbNoDataForZone = false;
    this.dbLoadingZones = true;

    this.supportService
      .getZonesByFacility(plant)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.dbLoadingZones = false;
          this.dbZones = zones ?? [];
          if (this.dbZones.length === 0) this.dbNoDataForPlant = true;
        },
        error: () => {
          this.dbLoadingZones = false;
          this.snackBar.open('Failed to load zones.', 'Close', { duration: 4000 });
        },
      });
  }

  onDbZoneChange(zone: ZoneConfig): void {
    this.dbSelectedZone = zone;
    this.dbSelectedSerial = '';
    this.dbSerials = [];
    this.dbNoDataForZone = false;
    this.dbLoadingSerials = true;

    this.supportService
      .getSerialsByFacilityAndZone(this.dbSelectedPlant, zone.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (serials) => {
          this.dbLoadingSerials = false;
          this.dbSerials = serials;
          if (serials.length === 0) this.dbNoDataForZone = true;
        },
        error: () => {
          this.dbLoadingSerials = false;
          this.snackBar.open('Failed to load serials.', 'Close', { duration: 4000 });
        },
      });
  }

  get dbDeleteDisabled(): boolean {
    return !this.dbSelectedSerial || this.dbDeleting;
  }

  deleteDatabaseInspection(): void {
    if (!this.dbSelectedZone || !this.dbSelectedSerial || !this.dbSelectedPlant) return;

    const serial = this.dbSelectedSerial;
    const zone = this.dbSelectedZone;
    const plant = this.dbSelectedPlant;

    const ref = this.dialogService.openDialog(
      'Permanently Delete Inspection from Database',
      `You are about to permanently delete ALL inspection data for serial #${serial} in "${zone.Name}" (plant ${plant}) from the database.\n\nThis includes all task executions, defects, deferrals, and attachments. If this serial is currently active in QBuilt, it will also be removed from the active inspections list.\n\nThis action is IRREVERSIBLE.\n\nAre you sure you want to proceed?`,
      'Cancel',
      'Permanently Delete',
      true,
    );

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;

      this.dbDeleting = true;
      this.supportService
        .deleteDatabaseInspection(serial, zone.Id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            const success = result?.data?.DeleteDatabaseInspection?.Success;
            const message = result?.data?.DeleteDatabaseInspection?.Message;
            if (!success) {
              this.dbDeleting = false;
              this.snackBar.open(`Delete failed: ${message ?? 'Unknown error'}`, 'Close', { duration: 6000 });
              return;
            }

            // PostgreSQL delete succeeded — check and clean up DynamoDB active inspections
            const pk = `${plant}-${zone.Id}`;
            this.supportService
              .getInspectionProgress(pk)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (record) => {
                  const finish = (dynamo: 'cleared' | 'none' | 'failed') => {
                    this.dbDeleting = false;
                    this.dbSerials = this.dbSerials.filter((s) => s !== serial);
                    this.dbSelectedSerial = '';
                    if (this.dbSerials.length === 0) this.dbNoDataForZone = true;

                    const msg =
                      dynamo === 'cleared'
                        ? `Serial #${serial} deleted from database and removed from active inspections.`
                        : dynamo === 'none'
                          ? `Serial #${serial} deleted from database. (Not found in active inspections.)`
                          : `Serial #${serial} deleted from database. Active inspection could not be cleared — please remove it manually.`;
                    this.snackBar.open(msg, 'Close', { duration: 7000 });
                  };

                  if (!record) {
                    finish('none');
                    return;
                  }

                  let activeData: any[];
                  try {
                    activeData =
                      typeof record.InspectionData === 'string'
                        ? JSON.parse(record.InspectionData)
                        : record.InspectionData ?? [];
                  } catch {
                    activeData = [];
                  }

                  const serialInActive = activeData.some(
                    (item) => (item.serialNumber ?? item.SerialNumber) === serial,
                  );

                  if (!serialInActive) {
                    finish('none');
                    return;
                  }

                  const remaining = activeData.filter(
                    (item) => (item.serialNumber ?? item.SerialNumber) !== serial,
                  );
                  const action$ =
                    remaining.length === 0
                      ? this.supportService.deleteInspection(pk)
                      : this.supportService.saveInspectionProgress(pk, remaining);

                  action$.pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => finish('cleared'),
                    error: () => finish('failed'),
                  });
                },
                error: () => {
                  this.dbDeleting = false;
                  this.dbSerials = this.dbSerials.filter((s) => s !== serial);
                  this.dbSelectedSerial = '';
                  if (this.dbSerials.length === 0) this.dbNoDataForZone = true;
                  this.snackBar.open(
                    `Serial #${serial} deleted from database. Active inspection check failed — please clear manually if needed.`,
                    'Close',
                    { duration: 7000 },
                  );
                },
              });
          },
          error: () => {
            this.dbDeleting = false;
            this.snackBar.open('Delete failed. Please try again.', 'Close', { duration: 4000 });
          },
        });
    });
  }

  // ── Create Checklist section handler ────────────────────────────────────────

  onCreateChecklist(): void {
    this.createChecklistError = null;
    this.createChecklistSuccess = false;

    if (!this.checklistJson.trim()) {
      this.createChecklistError = 'Payload is required.';
      return;
    }

    let parsedChecklist: any;
    try {
      parsedChecklist = JSON.parse(this.checklistJson);
    } catch {
      this.createChecklistError = 'Invalid JSON. Please check your payload.';
      return;
    }

    if (!parsedChecklist || typeof parsedChecklist !== 'object' || Array.isArray(parsedChecklist)
      || !parsedChecklist.Name || !Array.isArray(parsedChecklist.Stations)) {
      this.createChecklistError = 'Invalid checklist JSON. Object must include Name and Stations.';
      return;
    }

    this.isCreatingChecklist = true;

    this.qBuiltService
      .saveChecklist(new ChecklistValues(parsedChecklist))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isCreatingChecklist = false;
          if (!result?.Success) {
            this.createChecklistError = result?.Message ?? 'An error occurred. Please try again.';
            return;
          }
          this.createChecklistSuccess = true;
          this.checklistJson = '';
        },
        error: (err) => {
          this.isCreatingChecklist = false;
          this.createChecklistError = err?.message ?? 'An error occurred. Please try again.';
        },
      });
  }
}
